'use client';

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  footer?: React.ReactNode;
}

export function Modal({ isOpen, onClose, children }: ModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === overlayRef.current) onClose();
  };

  return createPortal(
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-[rgba(5,10,24,0.82)] backdrop-blur-[6px] animate-[fadeIn_0.25s_ease]"
    >
      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-[800px] animate-[slideUp_0.32s_cubic-bezier(0.22,1,0.36,1)]"
      >
        {children}

        {/* Close Button */}
        <button
          onClick={onClose}
          aria-label="Close popup"
          className="absolute -top-3.5 -right-3.5 w-9 h-9 rounded-full bg-white border border-slate-200 shadow-[0_4px_14px_rgba(0,0,0,0.18)] flex items-center justify-center z-10 transition duration-200 hover:bg-slate-100 hover:scale-110"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 1L13 13M13 1L1 13" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>,
    document.body
  );
}